import type { ProceduralBeat } from "@engine";

// Window in seconds (each side of a kick) for a "perfect" rotation.
// 90 ms is tight enough to feel skill-based but wide enough to be
// hittable on touch devices where input has its own latency floor.
const PERFECT_WINDOW_S = 0.09;
// Looser "good" window. The "good" rating still pays a bonus, just
// less, so casual players get positive feedback for being roughly on
// the beat without us giving away the perfect window.
const GOOD_WINDOW_S = 0.16;
// How long we keep a scheduled kick around in the ring buffer. A kick
// older than this can't possibly still be the nearest reference for
// any judge() call (we always have a more recent kick within 2s at
// 140 BPM half-time).
const KICK_RETENTION_S = 1.0;
// Fixed-size ring buffer. At 140 BPM the densest sections fire 2
// kicks per bar of half-time = ~1.7s per bar = ~1.2 kicks/s, so 8
// entries always covers KICK_RETENTION_S with headroom.
const RING_SIZE = 8;

export type RotationVerdict = "perfect" | "good" | "miss";

// Tracks the most recent and next-scheduled kick times (both in
// AudioContext seconds) so the score system can grade rotations
// against the beat clock without doing its own audio scheduling.
//
// Why a ring buffer of upcoming kicks? ProceduralBeat schedules each
// step ~150ms ahead of audio time (its SCHEDULE_AHEAD), so when the
// onStep callback fires for a kick, that kick is in the *future*. We
// keep both past and future kick times around and at judge() pick the
// nearest absolute delta, which gives us a symmetric perfect window
// regardless of whether the player rotated slightly before or after
// the actual audible kick.
export class BeatTimingService {
  private kickTimes: number[] = [];
  private size = 0;
  private head = 0;
  private unsub: (() => void) | null = null;

  constructor(beat: ProceduralBeat) {
    for (let i = 0; i < RING_SIZE; i++) this.kickTimes.push(0);
    this.unsub = beat.onStep.on((step) => {
      if (!step.isKick) return;
      this.kickTimes[this.head] = step.time;
      this.head = (this.head + 1) % RING_SIZE;
      if (this.size < RING_SIZE) this.size += 1;
    });
  }

  // Score a rotate-input against the nearest kick time. audioNowSec
  // should be `app.audio.context.currentTime` sampled in the input
  // handler so the latency delta matches what the player heard.
  judge(audioNowSec: number): RotationVerdict {
    const delta = this.nearestDelta(audioNowSec);
    if (delta < 0) return "miss";
    if (delta <= PERFECT_WINDOW_S) return "perfect";
    if (delta <= GOOD_WINDOW_S) return "good";
    return "miss";
  }

  // Public: returns the absolute delta (seconds) to the nearest kick,
  // or -1 if no kick is in range. Used by the HUD beat dot's "perfect
  // window" ring overlay so it visibly tightens as the next kick
  // approaches.
  nearestDelta(audioNowSec: number): number {
    if (this.size === 0) return -1;
    let best = Infinity;
    for (let i = 0; i < this.size; i++) {
      const t = this.kickTimes[i]!;
      const age = audioNowSec - t;
      // Drop entries that are either far in the past (will never be
      // the nearest reference again) or absurdly far in the future
      // (likely a stale entry from before a beat reset).
      if (age > KICK_RETENTION_S) continue;
      const d = Math.abs(age);
      if (d < best) best = d;
    }
    return Number.isFinite(best) ? best : -1;
  }

  dispose(): void {
    if (this.unsub) {
      this.unsub();
      this.unsub = null;
    }
    this.kickTimes.length = 0;
    this.size = 0;
    this.head = 0;
  }
}
